import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  LocationCity as CityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";
import { hasWritePermission } from '../../utils/menuFilter';

const City = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [cityData, setCityData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedCityForDelete, setSelectedCityForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [newCity, setNewCity] = useState({
    state: '',
    cityName: '',
    status: true,
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch states for dropdown
  const fetchStates = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=state`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const states = Array.isArray(result.data)
        ? result.data
            .filter((item) => item.status === true) // Only active states
            .map((item) => ({
              id: item._id,
              stateName: item.details?.stateName || 'Unknown',
            }))
        : [];

      setStateData(states);
    } catch (err) {
      console.error('Failed to load states:', err);
      setStateData([]);
    }
  };

  // Fetch cities
  const fetchCities = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=city`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const cities = Array.isArray(result.data)
        ? result.data.map((item) => ({
            id: item._id,
            cityId: item.details?.cityId || item._id.substring(0, 8).toUpperCase(),
            cityName: item.details?.cityName || 'Unknown',
            state: item.details?.state || '',
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          }))
        : [];

      setCityData(cities);
    } catch (err) {
      setApiError('Failed to load cities.');
      setCityData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchCities();
  }, []);

  // Extract unique states for filter
  const stateOptionsFromData = [...new Set(cityData.map((c) => c.state))]
    .filter(Boolean)
    .sort();

  // Filter logic
  const getFilteredCities = () => {
    return cityData.filter((c) => {
      const matchesSearch =
        c.cityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.state.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesState = !stateFilter || c.state === stateFilter;
      const matchesStatus = !statusFilter || c.status === statusFilter;
      return matchesSearch && matchesState && matchesStatus;
    });
  };

  const filteredCities = getFilteredCities();

  // Pagination
  const paginatedCities = filteredCities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newCity.state.trim()) err.state = 'State is required';
    if (!newCity.cityName.trim()) err.cityName = 'City name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return cityData.some(
      (c) =>
        c.cityName.toLowerCase() === newCity.cityName.trim().toLowerCase() &&
        c.state === newCity.state &&
        c.id !== editingCity?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingCity(null);
    setNewCity({
      state: '',
      cityName: '',
      status: true,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingCity(null);
    setNewCity({
      state: '',
      cityName: '',
      status: true,
    });
    setErrors({});
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setNewCity({
      state: city.state,
      cityName: city.cityName,
      status: city.rawStatus,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (city) => {
    setSelectedCityForDelete(city);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCityForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedCityForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'City deleted successfully!' });
      setAlertOpen(true);
      fetchCities(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete city.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedCityForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ cityName: 'City already exists for this state' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'city',
        details: {
          cityName: newCity.cityName.trim(),
          state: newCity.state,
        },
        status: newCity.status,
      };

      let response;
      if (editingCity) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editingCity.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingCity ? 'City updated successfully!' : 'City added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchCities(true);
    } catch (err) {
      setErrors({ cityName: 'Failed to save city.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStateFilter('');
    setStatusFilter('');
    setPage(0);
    fetchCities(true);
  };

  const handleChange = (field, value) => {
    setNewCity((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CityIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            City Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchCities()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add New City
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the city <strong>{selectedCityForDelete?.cityName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchCities()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Cities..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setPage(0);
                  }}
                  label="State"
                >
                  <MenuItem value="">All States</MenuItem>
                  {stateOptionsFromData.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredCities.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} cities matching your criteria.` : 'No cities found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !cityData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No cities found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCities.map((city, index) => (
                    <TableRow key={city.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{city.state}</TableCell>
                      <TableCell>{city.cityName}</TableCell>
                      <TableCell>
                        <Chip
                          label={city.status}
                          color={city.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(city)} disabled={isLoading}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(city)} disabled={isLoading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCities.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCity ? 'Edit City' : 'Add New City'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.state}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={newCity.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    label="State"
                  >
                    {stateData.map((state) => (
                      <MenuItem key={state.id} value={state.stateName}>
                        {state.stateName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.state && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.state}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="City Name"
                  value={newCity.cityName}
                  onChange={(e) => handleChange('cityName', e.target.value)}
                  error={!!errors.cityName}
                  helperText={errors.cityName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newCity.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingCity ? 'Update' : 'Add'} City
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default City;
